import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import clsx from "clsx";
import { Badge } from "catalyst-ui-kit/badge";
import Currency from "components/format/currency/currency";
import { ProductListRow } from "types/ProductCatalogue";
import { formatQuantity } from "./product-format";
import { ProductList, TypeFilter } from "./use-product-list";

/** The chips, left to right. */
const CHIPS: { filter: TypeFilter; labelKey: string }[] = [
  { filter: "all", labelKey: "all" },
  { filter: "service", labelKey: "products_page_services" },
  { filter: "product", labelKey: "products_page_goods" },
];

/** How far below the visible list (px) its end counts as "in view" and the next page is asked for. */
const NEAR_END_PX = 200;

interface Props {
  companyId: string;
  /** The product open on the right, if any. */
  selectedId: string | undefined;
  list: ProductList;
  onCreate: () => void;
  className?: string;
}

export default function ProductSidebar({ companyId, selectedId, list, onCreate, className }: Props) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  const endRef = useRef<HTMLLIElement>(null);

  // Asks for the next page while the end of the list is in view. A list too short to scroll keeps its end in view, so
  // the observer is set up again after every render, which asks once more. A list that is not on screen (the detail
  // is open on a narrow screen) never has its end in view, so loads nothing.
  useEffect(() => {
    const scroller = listRef.current;
    const end = endRef.current;
    if (!scroller || !end) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) list.loadMore();
      },
      { root: scroller, rootMargin: `${NEAR_END_PX}px` }
    );
    observer.observe(end);
    return () => observer.disconnect();
  });

  return (
    <aside className={clsx("w-full shrink-0 flex-col border-r border-gray-200 bg-white lg:w-80", className)}>
      <div className="border-b border-gray-100 p-3">
        <div className="relative">
          <Search aria-hidden className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={list.searchInput}
            onChange={(event) => list.setSearchInput(event.target.value)}
            placeholder={t("products_page_search_placeholder")}
            aria-label={t("search")}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:text-sm"
          />
        </div>
        <div role="group" aria-label={t("type")} className="mt-2.5 flex gap-1.5 text-xs">
          {CHIPS.map((chip) => (
            <button
              key={chip.filter}
              type="button"
              aria-pressed={list.typeFilter === chip.filter}
              onClick={() => list.setTypeFilter(chip.filter)}
              className={clsx(
                "rounded-full px-2.5 py-1 max-md:min-h-11 max-md:px-4 max-lg:py-2",
                list.typeFilter === chip.filter ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              )}
            >
              {t(chip.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <ul ref={listRef} className="flex-1 divide-y divide-gray-100 overflow-y-auto">
        {list.rows.map((row) => (
          <ProductRow key={row.id} row={row} href={`/app/${companyId}/products/${row.id}`} isSelected={row.id === selectedId} />
        ))}
        <ListFooter list={list} />
        <li ref={endRef} aria-hidden className="h-px" />
      </ul>

      <div className="border-t border-gray-100 p-3">
        <button
          type="button"
          onClick={onCreate}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 max-md:min-h-11"
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t("products_page_new")}
        </button>
      </div>
    </aside>
  );
}

function ProductRow({ row, href, isSelected }: { row: ProductListRow; href: string; isSelected: boolean }) {
  const { t } = useTranslation();
  const isService = row.type === "service";
  const unit = row.unit ? row.unit.title : null;

  return (
    <li>
      <Link
        to={href}
        aria-current={isSelected ? "page" : undefined}
        className={clsx(
          "block px-4 py-3 hover:bg-gray-50",
          isSelected && "border-l-4 border-indigo-600 bg-indigo-50 pl-3"
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium leading-snug text-gray-900">{row.title}</span>
          <Badge color={isService ? "zinc" : "amber"} className="shrink-0">
            {t(isService ? "products_page_type_service" : "products_page_type_product")}
          </Badge>
        </div>
        <div className="mt-1 flex justify-between gap-2 text-xs text-gray-500">
          <span className="font-mono">{row.sku}</span>
          {isService ? (
            <span>
              <Currency value={row.price} isSymbol isDecimal />
              {unit && `/${unit}`}
            </span>
          ) : (
            <span>
              {t("products_page_stock", { quantity: formatQuantity(row.quantity, unit) })}
              {!!row._reservedQuantity && ` · ${t("products_page_reserved_short", { quantity: formatQuantity(row._reservedQuantity) })}`}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

function ListFooter({ list }: { list: ProductList }) {
  const { t } = useTranslation();
  const isEmpty = list.rows.length === 0;
  const isFirstLoad = list.isLoading && isEmpty;

  let message: string | null = null;
  if (list.isError) message = t("products_page_list_failed");
  else if (isFirstLoad || list.isLoadingMore) message = t("loading");
  else if (isEmpty && !list.hasNextPage) message = t("products_page_nothing_found");
  if (!message) return null;

  return (
    <li className="p-6 text-center text-sm text-gray-400">
      {message}
      {list.isError && (
        <button type="button" onClick={list.reload} className="ml-2 text-indigo-600 hover:underline">
          {t("retry")}
        </button>
      )}
    </li>
  );
}
